import { Text } from '@chakra-ui/react';
import { InputNumber, Slider } from 'antd';
import React, { useEffect, useState } from 'react';

import { useDrivetrainFilter } from '@/hooks/filters/drivetrain-filter.hook';
import { useFeaturesFilter } from '@/hooks/filters/features-filter.hook';
import { useFuelConsumptionFilter } from '@/hooks/filters/fuel-consumption.hook';
import { useFuelTypeFilter } from '@/hooks/filters/fuel-type-filter.hook';
import { useMileageFilter } from '@/hooks/filters/mileage-filter.hook';
import { useTransmissionTypeFilter } from '@/hooks/filters/transmission-type-filter.hook';
import { useYearsFilterHook } from '@/hooks/filters/years-filter.hook';

import { FeaturesFilter } from '../../filters/features-filter/features-filter';
import { FilterBlocks } from '../../filters/filter-blocks/filter-blocks';
import { MobileFilter } from '../../filters/mobile-filter/mobile-filter';
import { YearsMileage } from '../../filters/years-mileage/years-mileage';
import { InputsSeparator } from '../price-finance/components/inputs-separator/inputs-separator';

import styles from './more-filters.module.scss';

export const MoreFilters: React.FC<{
  mobileVersion?: boolean;
  handleVisibleChange?: (isVisible: boolean) => void;
  isVisible?: boolean;
  onCloseMobileFilter?: () => void;
}> = ({
  mobileVersion,
  handleVisibleChange,
  isVisible,
  onCloseMobileFilter,
}) => {
  const {
    fuelTypeFilter,
    toggleFuelType,
    setFuelTypeFilter,
    visibleFuelTypeFilter,
  } = useFuelTypeFilter();
  const {
    transmissionTypeFilter,
    toggleTransmissionType,
    setTransmissionTypeFilter,
    visibleTransmissionTypeFilter,
  } = useTransmissionTypeFilter();
  const {
    drivetrainTypesFilter,
    toggleDrivetrainType,
    setDrivetrainTypesFilter,
    visibleDrivetrainTypeFilter,
  } = useDrivetrainFilter();
  const {
    consumptionFilterDefault,
    consumptionFilter,
    setConsumptionFilter,
    setSelectedConsumptionFilter,
  } = useFuelConsumptionFilter();
  const { yearsFilter, setYearsFilter } = useYearsFilterHook();
  const { mileageFilter, setMileageFilter } = useMileageFilter();
  const { featuresFilter, setFeaturesFilter } = useFeaturesFilter();
  const [years, setYears] = useState(yearsFilter);
  const [mileage, setMileage] = useState(mileageFilter);
  const [features, setFeatures] = useState(featuresFilter);
  const [fuel, setFuel] = useState(fuelTypeFilter);
  const [consumption, setConsumption] = useState(consumptionFilter);
  const [transmission, setTransmission] = useState(transmissionTypeFilter);
  const [drivetrain, setDrivetrain] = useState(drivetrainTypesFilter);

  useEffect(() => {
    if (isVisible) {
      setYears(yearsFilter);
      setMileage(mileageFilter);
      setFeatures(featuresFilter);
      setFuel(fuelTypeFilter);
      setConsumption(consumptionFilter);
      setTransmission(transmissionTypeFilter);
      setDrivetrain(drivetrainTypesFilter);
    }
  }, [isVisible]);

  const onClose = () => {
    setYearsFilter(years);
    setMileageFilter(mileage);
    setFeaturesFilter(features);
    setFuelTypeFilter(fuel);
    setConsumptionFilter(consumption);
    setTransmissionTypeFilter(transmission);
    setDrivetrainTypesFilter(drivetrain);
  };

  const marks = {
    [consumptionFilterDefault.min]: {
      label: (
        <Text className={styles.priceFormatText}>
          {consumptionFilterDefault.min} Litres
        </Text>
      ),
    },
    [consumptionFilterDefault.max]: {
      label: (
        <Text className={styles.priceFormatText}>
          {consumptionFilterDefault.max} Litres
        </Text>
      ),
    },
  };

  const fuelTypeFilters = visibleFuelTypeFilter;
  const transmissionTypeFilters = visibleTransmissionTypeFilter;
  const drivetrainTypesFilters = visibleDrivetrainTypeFilter;

  return (
    <MobileFilter
      title="More filters"
      handleVisibleChange={handleVisibleChange}
      isVisible={isVisible}
      onClose={onClose}
      onCloseMobileFilter={onCloseMobileFilter}
    >
      <div className={styles.wrapper}>
        {mobileVersion && (
          <div className={styles.mobileFilters}>
            <YearsMileage />
            <div className={styles.featuresFilterLabel}>
              <Text className={styles.labelText}>Features</Text>
            </div>
            <FeaturesFilter />
          </div>
        )}

        <div className={styles.filterBlocks}>
          <FilterBlocks
            label="Fuel Type"
            filters={fuelTypeFilters}
            onSelect={toggleFuelType}
          />
        </div>

        <div className={styles.filterBlocks}>
          <div className={styles.rangeWrapper}>
            <div className={styles.filterLabel}>
              <Text className={styles.labelText}>Litres/100km</Text>
            </div>
            <div className={styles.rangeInputs}>
              <InputNumber
                className={styles.rangeInput}
                value={consumptionFilter.min || consumptionFilterDefault.min}
                onChange={(val) => {
                  const { min, max } = consumptionFilterDefault;

                  if (
                    !val ||
                    val < min ||
                    val > max ||
                    val >= consumptionFilter.max
                  ) {
                    setConsumptionFilter((prevState) => ({
                      ...prevState,
                      min,
                    }));
                    return;
                  }

                  setConsumptionFilter((prevState) => ({
                    ...prevState,
                    min: val as number,
                  }));
                }}
              />
              <InputsSeparator className={styles.separator} />
              <InputNumber
                className={styles.rangeInput}
                value={consumptionFilter.max || consumptionFilterDefault.max}
                onChange={(val) => {
                  const { min, max } = consumptionFilterDefault;

                  if (
                    !val ||
                    val < min ||
                    val > max ||
                    val <= consumptionFilter.min
                  ) {
                    setConsumptionFilter((prevState) => ({
                      ...prevState,
                      max,
                    }));
                    return;
                  }

                  setConsumptionFilter((prevState) => ({
                    ...prevState,
                    max: val as number,
                  }));
                }}
              />
            </div>
          </div>
          <Slider
            className={styles.sliderRange}
            range
            marks={marks}
            step={1}
            max={consumptionFilterDefault.max}
            min={consumptionFilterDefault.min}
            value={[
              consumptionFilter.min || consumptionFilterDefault.min,
              consumptionFilter.max || consumptionFilterDefault.max,
            ]}
            tipFormatter={null}
            onChange={([min, max]) => {
              if (typeof min !== 'undefined' && typeof max !== 'undefined')
                setConsumptionFilter({
                  min,
                  max,
                });
            }}
            onAfterChange={() => {
              setSelectedConsumptionFilter(consumptionFilter);
            }}
          />
        </div>

        <div className={styles.filterBlocks}>
          <FilterBlocks
            label="Transmission"
            filters={transmissionTypeFilters}
            onSelect={toggleTransmissionType}
          />
        </div>

        <div className={styles.filterBlocks}>
          <FilterBlocks
            label="Drive Type"
            filters={drivetrainTypesFilters}
            onSelect={toggleDrivetrainType}
          />
        </div>
      </div>
    </MobileFilter>
  );
};
